import io from 'socket.io-client';
import { SocketConfig } from './socket.types';

class SocketV1Service {
  private static instance: SocketV1Service;
  private socket: SocketIOClient.Socket | null = null;
  private config: SocketConfig;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private listeners: Map<string, Array<(...args: any[]) => void>> = new Map();
  private eventEmitter: Map<string, Array<(...args: any[]) => void>> = new Map();

  private constructor(config: SocketConfig) {
    this.config = config;
  }

  static getInstance(config?: SocketConfig): SocketV1Service {
    if (!SocketV1Service.instance) {
      if (!config) {
        throw new Error('[SocketV1] Config is required for first initialization');
      }
      SocketV1Service.instance = new SocketV1Service(config);
    }
    return SocketV1Service.instance;
    
  }

  static resetInstance(): void {
    if (SocketV1Service.instance) {
      SocketV1Service.instance.cleanup();
      SocketV1Service.instance = null as any;
    }
  }

  private cleanup(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
    this.eventEmitter.clear();
  }

  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    try {
      const defaultOptions = {
        transports: ['websocket', 'polling'] as Array<'websocket' | 'polling'>,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
        pingTimeout: 60000,
        pingInterval: 25000,
      };

      this.socket = io(this.config.url, {
        ...defaultOptions,
        ...this.config.options,
      });

      this.setupDefaultListeners();
      this.reattachListeners();
      
    } catch (error) {
      console.error('[SocketV1] Connection error:', error);
      this.emitEvent('error', error);
      throw error;
    }
  }

  private setupDefaultListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
      this.emitEvent('connect');
    });

    this.socket.on('disconnect', (reason: string) => {
      this.emitEvent('disconnect', reason);
      
      if (reason === 'io server disconnect') {
        this.socket?.connect();
      }
    });
  }

  private reattachListeners(): void {
    if (!this.socket) return;

    this.listeners.forEach((handlers, event) => {
      handlers.forEach(handler => {
        this.socket?.on(event, handler);
      });
    });
  }

  private emitEvent(event: string, ...args: any[]): void {
    const handlers = this.eventEmitter.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(...args);
        } catch (error) {
          console.error(`[SocketV1] Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  emit(event: string, ...args: any[]): void {
    if (!this.socket) {
      console.warn('[SocketV1] Cannot emit - socket not initialized');
      return;
    }

    if (!this.socket.connected) {
      console.warn('[SocketV1] Cannot emit - not connected. Event:', event);
      return;
    }

    this.socket.emit(event, ...args);
  }

  on<T = any>(event: string, handler: (data: T) => void): void {
    if (!this.socket) {
      console.warn('[SocketV1] Cannot add listener - socket not initialized');
      return;
    }

    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)?.push(handler);

    this.socket.on(event, handler);
  }

  once<T = any>(event: string, handler: (data: T) => void): void {
    if (!this.socket) {
      console.warn('[SocketV1] Cannot add one-time listener - socket not initialized');
      return;
    }

    this.socket.once(event, handler);
  }

  off(event: string, handler?: (...args: any[]) => void): void {
    if (!this.socket) return;

    if (handler) {
      this.socket.off(event, handler);
      
      const handlers = this.listeners.get(event);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
        if (handlers.length === 0) {
          this.listeners.delete(event);
        }
      }
    } else {
      this.socket.off(event);
      this.listeners.delete(event);
    }
  }

  removeAllListeners(event?: string): void {
    if (!this.socket) return;

    if (event) {
      this.socket.removeAllListeners();
      this.listeners.delete(event);
    } else {
      this.socket.removeAllListeners();
      this.listeners.clear();
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  getSocket(): SocketIOClient.Socket | null {
    return this.socket;
  }

  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  getReconnectAttempts(): number {
    return this.reconnectAttempts;
  }

  // Event emitter methods para comunicação interna
  addEventListener(event: string, handler: (...args: any[]) => void): void {
    if (!this.eventEmitter.has(event)) {
      this.eventEmitter.set(event, []);
    }
    this.eventEmitter.get(event)?.push(handler);
  }

  removeEventListener(event: string, handler?: (...args: any[]) => void): void {
    if (handler) {
      const handlers = this.eventEmitter.get(event);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
        if (handlers.length === 0) {
          this.eventEmitter.delete(event);
        }
      }
    } else {
      this.eventEmitter.delete(event);
    }
  }

}

export default SocketV1Service;
